import type { CVData } from "../types";

export function CVTemplateMinimal({ data }: { data: CVData }) {
  const { profile, experiences, education, skills, certificates } = data;

  return (
    <div
      className="cv-content mx-auto max-w-[700px] bg-white p-12 text-gray-700 print:p-10"
      style={{
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 13,
        lineHeight: 1.7,
      }}
    >
      <div className="border-b border-gray-200 pb-6">
        <h1 className="text-2xl font-light tracking-tight text-gray-900">
          {profile.name}
        </h1>
        <p className="mt-1 text-sm text-gray-500">{profile.title}</p>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
          {profile.email && <span>{profile.email}</span>}
          {profile.phone && <span>{profile.phone}</span>}
          {profile.address && <span>{profile.address}</span>}
          {profile.linkedin_url && <span>LinkedIn</span>}
          {profile.github_url && <span>GitHub</span>}
        </div>
      </div>

      {profile.bio && (
        <section className="mt-6">
          <h2 className="text-xs font-medium uppercase tracking-widest text-gray-400">
            Summary
          </h2>
          <p className="mt-2 text-gray-600">{profile.bio}</p>
        </section>
      )}

      {experiences.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xs font-medium uppercase tracking-widest text-gray-400">
            Experience
          </h2>
          <div className="mt-3 space-y-4">
            {experiences.map((exp) => (
              <div key={exp.id} className="border-l-2 border-gray-200 pl-4">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-900">{exp.position}</span>
                  <span className="text-xs text-gray-400">
                    {exp.period_start} – {exp.period_end || "Now"}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{exp.company}</p>
                <ul className="mt-1 list-[circle] space-y-0.5 pl-3 text-gray-600">
                  {exp.responsibilities.map((r, i) => (
                    <li key={i} className="text-[12px]">{r}</li>
                  ))}
                </ul>
                <div className="mt-2 flex flex-wrap gap-1">
                  {exp.tech_stack.map((t) => (
                    <span
                      key={t}
                      className="rounded border border-gray-200 px-2 py-0.5 text-[10px] text-gray-500"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {education.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xs font-medium uppercase tracking-widest text-gray-400">
            Education
          </h2>
          <div className="mt-3 space-y-2">
            {education.map((edu) => (
              <div key={edu.id} className="flex justify-between">
                <div>
                  <span className="font-medium text-gray-900">{edu.school}</span>
                  <span className="text-gray-600"> — {edu.degree}</span>
                  {edu.grade && (
                    <span className="text-gray-500"> ({edu.grade})</span>
                  )}
                </div>
                <span className="text-xs text-gray-400">{edu.period}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {skills.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xs font-medium uppercase tracking-widest text-gray-400">
            Skills
          </h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {skills.map((s) => (
              <span
                key={s.id}
                className="text-[12px] text-gray-600"
              >
                {s.name}
                <span className="ml-1 text-gray-300">({s.level}%)</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {certificates.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xs font-medium uppercase tracking-widest text-gray-400">
            Certificates
          </h2>
          <div className="mt-2 space-y-1 text-[12px] text-gray-600">
            {certificates.map((c) => (
              <div key={c.id}>
                {c.name}
                {c.date && <span className="text-gray-400"> · {c.date}</span>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
